import { log } from '@/lib/log';

// Service-worker registration — registration ONLY. There is deliberately no
// update-prompt flow.
//
// Atlas's app shell is network-first for navigations, so a page load already
// carries the newest deployed build and there is nothing to prompt about.
// [sw.ts](../sw.ts) sets `navigationPreload: true`, and serwist's
// `PrecacheStrategy._handle` returns the navigation-preload response BEFORE it
// consults the precache — so a navigation is answered by the network, and the
// precache answers when the network can't, i.e. offline.
//
// Measured 2026-09-01 by swapping two real builds under the running Express
// server with a build-marker attribute in the shell: after a "deploy", a
// reload on `/home` AND a cold navigation to `/` (the PWA start_url) both
// rendered the NEW build while the new SW was still parked in `waiting`. The
// old `<PwaUpdateBanner>` therefore fired on the very load that had already
// applied the update — it announced an update the user was already looking at.
//
// THE CAVEAT, because that mechanism is conditional and not universal:
// `getPreloadResponse()` needs `'preloadResponse' in event`, and serwist's
// `enableNavigationPreload()` no-ops on an engine with no
// `registration.navigationPreload`. There, the precache answers navigations,
// and `/` — which the precache route claims via `directoryIndex:
// 'index.html'` — serves the build its SW shipped with until every tab of the
// origin closes. Deep links and in-app routing are unaffected (no precache
// entry matches `/home`, and no `navigateFallback` is registered). Checked
// 2026-09-01: `NavigationPreloadManager` is present in Chromium, Firefox 153
// and WebKit 26.5, so all three take the network path; end-to-end serving was
// verified in CHROMIUM ONLY, because Playwright's firefox and webkit never
// hand back a controlled document (a harness limit, not product behaviour).
// A sufficiently old engine can therefore still pin `/` to a stale build, and
// the banner used to be its only escape. If that ever shows up in the field,
// the fix is to stop the precache route claiming navigations (drop
// `directoryIndex` / `cleanURLs` and register an explicit network-first
// navigation route with a precache fallback) — not to bring the banner back.
//
// `skipWaiting` stays `false` in `sw.ts` on purpose: the incoming SW parks in
// 'waiting' until every tab of the origin closes, so a long-lived tab keeps
// its own build's precached chunks instead of 404ing on lazy-route imports
// that the deploy has already deleted from the server.

let registered = false;

/**
 * Whether to register the service worker. Pure + testable (Atlas remediation #20).
 *
 * Skips the Vite dev server (mode 'development'), where Serwist is disabled
 * (`vite.config.ts` `disable: mode === 'development'`) so `/sw.js` returns the
 * dev index HTML and registration fails — noisy `pwa.sw_register_error` lines
 * on every dev load. Tests run under mode 'test' and prod builds under
 * 'production', so both still register.
 */
export function shouldRegisterServiceWorker(opts: { mode: string; hasServiceWorker: boolean }): boolean {
    return opts.hasServiceWorker && opts.mode !== 'development';
}

/**
 * Register `/sw.js` once, after the page has loaded.
 *
 * Deferring to the `load` event is the one behaviour worth keeping from
 * `@serwist/window` (which this replaced): the SW install precaches ~2.3 MB,
 * and starting that while the first paint is still in flight competes with it.
 */
export function registerServiceWorker(): void {
    if (registered) return;
    const hasServiceWorker = typeof navigator !== 'undefined' && 'serviceWorker' in navigator;
    if (!shouldRegisterServiceWorker({ mode: import.meta.env.MODE, hasServiceWorker })) return;
    registered = true;

    const register = (): void => {
        navigator.serviceWorker
            .register('/sw.js', { scope: '/', type: 'classic' })
            .then((reg) => {
                log.info('pwa.sw_registered', { scope: reg.scope });
            })
            .catch((error: unknown) => {
                log.warn('pwa.sw_register_error', {
                    error: (error as Error)?.message ?? String(error),
                });
            });
    };

    if (typeof document !== 'undefined' && document.readyState === 'complete') register();
    else window.addEventListener('load', register, { once: true });
}

/**
 * Test hook — drops the once-only latch so the next `registerServiceWorker()`
 * call re-runs registration. Module-level singletons need an `afterEach` drain
 * hook in every consumer's test file (Phase 4 retro #5).
 */
export function _resetServiceWorkerForTests(): void {
    registered = false;
}
